import { getIn, useFormikContext } from 'formik'
import type { FieldConfig as FormikFieldConfig } from 'formik/dist/Field'
import React, { ChangeEvent, useRef, useState, type JSX } from 'react'

import {
  preventNonDecimalInputChars,
  preventNonDecimalPaste,
  preventNonIntegerInputChars,
  preventNonIntegerPaste,
} from '@pages/Trade/utils/helpers'

import { Input } from '@ui/components/Input/Input'
import { IconFCType } from '@ui/icons/types'

import { isZero } from '@utils/validations'

import { DeepNonNullable } from '../types/DeepNonNullable'
import type { DeepRequired } from '../types/DeepRequired'
import type { TypedFieldProps } from '../types/TypedFieldProps'
import type { ValidateField } from '../types/ValidateField'
import { getLabel, getName, getPlaceholder } from './helpers'

interface FormFieldProps extends Omit<FormikFieldConfig, 'name' | 'value' | 'children'> {
  namePrefix?: string
  helpText?: string
  highlighted?: boolean
  leftText?: string
  leftIcon?: IconFCType
  rightIcon?: IconFCType
  hint?: string | React.ReactNode
  min?: number
  max?: number
  rightText?: string
  decimalPlaces?: number
  onChange?(
    value: React.ChangeEvent<HTMLInputElement>,
    callback: (event: ChangeEvent<HTMLInputElement>) => void
  ): void
  onBlur?(
    value: React.ChangeEvent<HTMLInputElement>,
    callback: (event: ChangeEvent<HTMLInputElement>) => void
  ): void
  onClick?(): void
  fullWidth?: boolean
}

export function RawFormNumericField<FormValues>(
  props: FormFieldProps & TypedFieldProps<FormValues>
): JSX.Element {
  const {
    namePrefix = '',
    name = '',
    min,
    max,
    leftIcon,
    rightText,
    rightIcon,
    hint,
    onChange,
    onBlur,
    decimalPlaces,
    fullWidth,
    ...inputProps
  } = props

  const fieldRef = useRef<HTMLDivElement>(null)
  const [valueState, setValueState] = useState<string | undefined>(undefined)
  const context = useFormikContext<FormValues>()

  const value = getIn(context.values, name)

  const error = getIn(context.errors, name)
  const touched = getIn(context.touched, name)

  const label = getLabel(props)
  const placeholder = getPlaceholder(props)

  const labelProps = label ? { label, placeholder } : { label: undefined, placeholder: undefined }

  const showHint = touched && error ? error : hint

  const isIntegerOnly = decimalPlaces === 0
  const handleKeyDown = isIntegerOnly ? preventNonIntegerInputChars : preventNonDecimalInputChars
  const handlePaste = isIntegerOnly ? preventNonIntegerPaste : preventNonDecimalPaste

  const handleFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    const pattern =
      decimalPlaces !== undefined ? new RegExp(`^\\d*\\.?\\d{0,${decimalPlaces}}$`) : /^\d*\.?\d*$/

    if (value !== '' && !pattern.test(value)) {
      return
    }

    const eventWithLimitedDecimals = {
      ...event,
      target: { ...event.target, value },
    }

    if (onChange) {
      onChange(eventWithLimitedDecimals, handleField(eventWithLimitedDecimals))
      setValueState(value)
    } else {
      handleField(eventWithLimitedDecimals)()
      setValueState(value)
    }
  }

  const handleFieldBlur = (event: ChangeEvent<HTMLInputElement>) => {
    if (onBlur) {
      onBlur(event, handleField(event))
      setValueState(undefined)
    } else {
      handleField(event)()
      setValueState(undefined)
    }
  }

  const handleField = (event: ChangeEvent<HTMLInputElement>) => () => {
    const value = Number.parseFloat(event.target.value)

    context.setFieldTouched(name)

    if (isNumericFieldMinValid(value, min)) {
      context.setFieldValue(name, min)
    } else {
      context.setFieldValue(name, !isNaN(value) ? value : undefined)
    }
  }

  return (
    <Input
      name={getName(namePrefix, props)}
      {...inputProps}
      {...labelProps}
      value={getValue(valueState, value) || ''}
      type='number'
      inputMode={isIntegerOnly ? 'numeric' : 'decimal'}
      min={min !== undefined && !isNaN(min) ? min : undefined}
      max={max !== undefined && !isNaN(max) ? max : undefined}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onChange={handleFieldChange}
      onBlur={handleFieldBlur}
      onWheel={(event: React.WheelEvent<HTMLInputElement>) => event.currentTarget.blur()}
      leftIcon={leftIcon}
      rightIcon={rightIcon}
      error={!!error && touched}
      hint={showHint}
      boxRef={fieldRef}
      fullWidth={fullWidth}
      rightText={rightText}
    />
  )
}

const getValue = (valueState: string | number | undefined, value: string | number) => {
  if (valueState === '') {
    return undefined
  }
  if (typeof valueState === 'number' && valueState <= 0) {
    return valueState
  }
  return valueState || value
}

export const isNumericFieldMinValid = (value: number, min = 0): boolean => {
  const hasMin = min || isZero(min)
  return !!(hasMin && value < min)
}

export const isNumericFieldMaxValid = (value: number, max = 0): boolean => {
  const hasMax = max || isZero(max)
  return !!(hasMax && value > max)
}

export const isNumericFieldRangeValid = (value = 0, min: number, max: number): boolean => {
  if (isNaN(value) || value === undefined || value === null) {
    return false
  }

  const isMinValid = !isNumericFieldMinValid(value, min)
  const isMaxValid = !isNumericFieldMaxValid(value, max)

  return isMinValid && isMaxValid
}

export function createFormNumericField<FormValues>() {
  return function TypedField<Name extends ValidateField<DeepNonNullable<DeepRequired<FormValues>>>>(
    props: FormFieldProps & TypedFieldProps<DeepNonNullable<DeepRequired<FormValues>>, Name>
  ): JSX.Element {
    return <RawFormNumericField {...props} />
  }
}
